import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { buildNavItems, COMMON_NAV_ITEMS } from '@/app/routing/builders/nav';
import { useAuth, useServicePermission } from '@/features/auth/hooks';
import { useBootstrap } from '@/features/auth/hooks/useBootstrap';
import { sortBootstrapShops } from '@/features/auth/utils/sort-bootstrap-shops';
import type { BootstrapShop } from '@/shared/api/bootstrap';
import type { User } from '@/shared/types';
import type { MenuItem, UserType } from '../../side-menu/types';
import { extractShopSlugFromPath, mapRoleToUserType, resolveEffectiveShopSlug } from '../utils';

export interface NavigationIdentity {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  userType: UserType;
  shopSlug: string | undefined;
  shops: BootstrapShop[];
  menuItems: MenuItem[];
  commonItems: MenuItem[];
  logout: () => void;
}

export const useNavigationIdentity = (): NavigationIdentity => {
  const { isAuthenticated, isLoading, user, role, logout, accessToken } = useAuth();
  const location = useLocation();
  const { data: bootstrapData } = useBootstrap({
    enabled: isAuthenticated && !isLoading && !!accessToken,
    accessToken,
  });

  const urlShopSlug = extractShopSlugFromPath(location.pathname);
  // Memo the empty-array fallback so dependent memos don't re-run
  // every render when bootstrap is still loading.
  const shops = useMemo(
    () => bootstrapData?.shops ?? [],
    [bootstrapData?.shops],
  );
  // Defensive sort before picking a default shop slug for the nav.
  // The URL slug wins when present; this only matters for the
  // "logged in, not yet inside a /shops/:slug route" case.
  const bootstrapShopSlug = useMemo(
    () => sortBootstrapShops(shops)[0]?.slug,
    [shops],
  );

  // Derive effective role from shop-scoped bootstrap data when in a shop context.
  // This ensures the nav reflects the user's role in the active shop, not their global role.
  const activeShopSlug = urlShopSlug || bootstrapShopSlug;
  const activeShop = shops.find(s => s.slug === activeShopSlug);
  // Pass activeShop.id so staff permission resolves by the membership for
  // the current shop, not an arbitrary first-active membership.
  const hasServicePermission = useServicePermission(
    activeShop?.role,
    activeShop?.id,
  );
  const effectiveRole = activeShop
    ? activeShop.role === 'OWNER' ? 'shop_owner'
      : activeShop.role === 'MANAGER' ? 'shop_manager'
      : activeShop.role === 'STAFF' ? 'staff_provider'
      : activeShop.role === 'RENTER' ? 'staff_provider'
      : role
    : role;
  const userType = mapRoleToUserType(effectiveRole);

  const shopSlug = resolveEffectiveShopSlug(userType, urlShopSlug, bootstrapShopSlug);

  const menuItems = useMemo(
    () => buildNavItems(userType, shopSlug, { hasServicePermission }),
    [userType, shopSlug, hasServicePermission],
  );

  const commonItems = useMemo(
    () => COMMON_NAV_ITEMS.filter(item => item.roles?.includes(userType)),
    [userType],
  );

  // Merge the provider's DB profile (pictureUrl, name) into the Auth0 user object
  // so the sidebar shows the actual uploaded profile image, not the Auth0 gravatar
  const enrichedUser = useMemo<User | null>(() => {
    if (!user) return null;
    const dbPictureUrl = bootstrapData?.user?.pictureUrl;
    const dbName = bootstrapData?.user?.name;
    return {
      ...user,
      pictureUrl: dbPictureUrl || user.pictureUrl,
      name: dbName || user.name,
    };
  }, [user, bootstrapData?.user?.pictureUrl, bootstrapData?.user?.name]);

  return { isAuthenticated, isLoading, user: enrichedUser, userType, shopSlug, shops, menuItems, commonItems, logout };
};
